import { z } from 'zod';
export const PROVISIONING_ACTIONS = [
    'sync_entitlements',
    'enable_module',
    'disable_module',
    'update_capacity_limits',
    'apply_license_key',
    'issue_grace_extension',
    'revoke_access',
    'set_commercial_status',
    'grant_tester_access',
    'create_test_hospital',
];
export const ProvisioningActionSchema = z.enum(PROVISIONING_ACTIONS);
/**
 * Actions that take something away from a hospital.
 *
 * These are the ones where a mistake is felt by clinicians mid-shift: a module
 * disappearing from the sidebar, a bed ceiling dropping below the current
 * census, or access being withdrawn outright. They require a second approver
 * (`entitlement.reduce`, which the approvals module already understands)
 * before the command may be queued. Everything else — enabling a module,
 * raising a limit, converting a trial to a paid contract — is additive and a
 * single authorised operator may dispatch it.
 *
 * `set_commercial_status` is deliberately NOT in this list even though it can
 * carry `suspended`: the suspension path is guarded separately in the service,
 * because the same action is overwhelmingly used for the benign trial → active
 * conversion and forcing two-person approval on every conversion would train
 * operators to treat the approval as noise.
 */
export const REDUCING_PROVISIONING_ACTIONS = [
    'disable_module',
    'revoke_access',
];
export function isReducingAction(action) {
    return REDUCING_PROVISIONING_ACTIONS.includes(action);
}
/**
 * Commercial lifecycle states a tenant node may be moved into.
 *
 * Mirrors `Organization.subscriptionStatus` in the HospaSense tenant schema.
 * Kept as a literal union rather than a free string so a typo cannot put a
 * hospital into a state its own gate does not recognise.
 */
export const COMMERCIAL_STATUSES = ['trial', 'active', 'past_due', 'suspended'];
export const CommercialStatusSchema = z.enum(COMMERCIAL_STATUSES);
/**
 * Payload for `set_commercial_status`.
 *
 * This is the demo-to-paying switch. Until now the tenant's `subscriptionStatus`
 * moved on exactly one trigger — a paid Stripe invoice — so a hospital that
 * signed a contract offline (the normal case for Indian hospitals paying by
 * NEFT against a PO) had no route out of `trial` except a hand-edited database
 * row. This carries the decision as a signed, audited, replayable command.
 *
 * `effectiveFrom` exists so a conversion can be dated to the contract start
 * rather than to the moment an operator happened to click, which is what the
 * finance team reconciles against.
 */
export const SetCommercialStatusPayloadSchema = z
    .object({
    status: CommercialStatusSchema,
    /** ISO date the commercial change takes effect. Defaults to now on the node. */
    effectiveFrom: z.iso.datetime().optional(),
    /** Control-side contract this conversion realises, for cross-referencing. */
    contractId: z.uuid().optional(),
    /** Next billing anchor, when the contract defines one. */
    nextBillingDate: z.iso.datetime().optional(),
})
    .strict();
/**
 * Payload for `grant_tester_access` — Control's "Give tester access" button.
 *
 * The node sets one password on every role login in a SenseSys evaluation
 * organisation, signs in as each of them to prove it, and only then emails the
 * sheet to the tester. The password never comes back to Control; the ack
 * carries counts ("35 of 35 logins working, emailed").
 *
 * `tenantOrganisationId` is the hospital's id ON THE TENANT, which is not
 * always Control's id for it. It sits in the payload so the command signature
 * covers it. The node refuses any organisation with a non-SenseSys Super Admin,
 * so a customer hospital cannot be given a shared password even by mistake.
 */
export const TESTER_EMAIL_DOMAIN = 'sensesys.org';
export const GrantTesterAccessPayloadSchema = z
    .object({
    testerEmail: z
        .email()
        .transform((v) => v.trim().toLowerCase())
        .refine((v) => v.endsWith(`@${TESTER_EMAIL_DOMAIN}`), {
        message: `Tester access can only be sent to a @${TESTER_EMAIL_DOMAIN} address.`,
    }),
    tenantOrganisationId: z.uuid(),
})
    .strict();
/** What Control shows after a tester-access grant, read from the node's ack. */
export const TesterAccessGrantViewSchema = z.object({
    commandId: z.uuid(),
    /** `build` = "New test hospital" (hospital built, then access given). */
    kind: z.enum(['grant', 'build']),
    testerEmail: z.string(),
    status: z.enum([
        'queued',
        'dispatched',
        'acknowledged',
        'failed',
        'timed_out',
        'cancelled',
        'reconciled',
    ]),
    requestedAt: z.string(),
    completedAt: z.string().nullable(),
    seatsTotal: z.number().int().nullable(),
    seatsVerified: z.number().int().nullable(),
    emailed: z.boolean().nullable(),
    error: z.string().nullable(),
});
export const PROVISIONING_STATUSES = [
    'queued',
    'dispatched',
    'acknowledged',
    'failed',
    'reconciled',
    'timed_out',
    'cancelled',
];
export const ProvisioningStatusSchema = z.enum(PROVISIONING_STATUSES);
/**
 * Outbox provisioning command model.
 */
export const ProvisioningCommandSchema = z.object({
    id: z.uuid(),
    organisationId: z.uuid(),
    organisationName: z.string().optional(),
    installationId: z.string(),
    action: ProvisioningActionSchema,
    payload: z.record(z.string(), z.unknown()),
    signedSignature: z.string(),
    idempotencyKey: z.string(),
    status: ProvisioningStatusSchema,
    attempts: z.number().int().nonnegative().default(0),
    maxAttempts: z.number().int().default(5),
    nextAttemptAt: z.iso.datetime().nullable().optional(),
    dispatchedAt: z.iso.datetime().nullable().optional(),
    acknowledgedAt: z.iso.datetime().nullable().optional(),
    ackPayload: z.record(z.string(), z.unknown()).nullable().optional(),
    errorMessage: z.string().nullable().optional(),
    approvalRequestId: z.uuid().nullable().optional(),
    initiatedByUserId: z.uuid(),
    initiatedByDisplayName: z.string().nullable().optional(),
    reason: z.string(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
    version: z.number().int().min(1),
});
/**
 * Dispatch command payload.
 */
export const ProvisioningCommandCreateSchema = z
    .object({
    organisationId: z.uuid(),
    installationId: z.string().min(1),
    action: ProvisioningActionSchema,
    payload: z.record(z.string(), z.unknown()),
    reason: z.string().trim().min(5).max(1000),
    approvalRequestId: z.uuid().optional(),
})
    .strict();
/**
 * Remote acknowledgement payload from connector/heartbeat webhook.
 */
export const ProvisioningAckSchema = z
    .object({
    commandId: z.uuid(),
    idempotencyKey: z.string(),
    success: z.boolean(),
    resultDetails: z.record(z.string(), z.unknown()).optional(),
    errorCode: z.string().optional(),
    errorMessage: z.string().optional(),
    appliedAt: z.iso.datetime(),
})
    .strict();
/**
 * ── Node-facing channel ──────────────────────────────────────────────────
 *
 * Everything above is operator-facing: an authenticated human in Control
 * queueing a command. The schemas below are what a *tenant node* sees, and
 * they exist because the outbox previously had no delivery mechanism at all —
 * commands were queued, marked dispatched, and nothing ever collected them.
 *
 * The node PULLS. It is not pushed to, for three reasons that all matter more
 * than the extra round trip:
 *
 *   1. An on-premises hospital sits behind its own firewall with no inbound
 *      port we can reach, and asking hospital IT to open one is both a
 *      security conversation and a support burden. Outbound HTTPS they
 *      already have.
 *   2. Pull means the node decides when it is ready to apply a change, so a
 *      command cannot land mid-migration or during a restart.
 *   3. A node that has been decommissioned simply stops polling. There is no
 *      queue of undeliverable pushes retrying against a dead host.
 *
 * Authentication is the installation's HMAC signing key, which the fleet
 * module already issues and stores per installation. The node signs its own
 * request; Control verifies with the stored secret. No bearer token is minted,
 * so there is nothing to leak from a hospital's environment that would grant
 * more than that one installation's own commands.
 */
/** A command as delivered to the node. Operator identity is deliberately omitted. */
export const NodeProvisioningCommandSchema = z.object({
    id: z.uuid(),
    organisationId: z.uuid(),
    installationId: z.string(),
    action: ProvisioningActionSchema,
    payload: z.record(z.string(), z.unknown()),
    /** HMAC over {organisationId, installationId, action, payload}; the node re-computes it. */
    signature: z.string(),
    idempotencyKey: z.string(),
    issuedAt: z.iso.datetime(),
});
export const NodeProvisioningPullResponseSchema = z.object({
    commands: z.array(NodeProvisioningCommandSchema),
    /** Server clock, so a node can detect its own drift before it rejects signatures. */
    serverTime: z.iso.datetime(),
});
//# sourceMappingURL=provisioning.js.map